import {
  Box,
  Button,
  ButtonBase,
  ButtonGroup,
  Chip,
  Collapse,
  Grid,
  Icon,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  IconChevronRight,
  IconCloudDownload,
  IconTrashX,
  IconFileTypeCsv,
  IconFileSearch,
} from "@tabler/icons-react";
import { useUsuario } from "../../../../providers/usuarioProvider";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { deleteDoc } from "firebase/firestore";
import UploadInternacionalDb from "../../../../db/uploadInternacionalDb";
import UploadInternacional from "../../../../models/uploadInternacional";
import { map } from "lodash";
import { DebitoCredito } from "../../../../models/enum";
import moment from "moment-timezone";
import OperacaoInternacional from "../../../../models/operacaoInternacional";
import OperacaoInternacionalDb from "../../../../db/operacaoInternacionalDb";
import { onSnapshot, query, where } from "firebase/firestore";
import { Link } from "react-router-dom";

type Props = {
  upload: UploadInternacional;
};

const PRAZO_PROCESSAMENTO_MIN = 15;

export default function StatementImportado({ upload }: Props) {
  const { visualizado } = useUsuario();
  const [expandido, setExpandido] = useState(false);
  const [operacoes, setOperacoes] = useState<OperacaoInternacional[]>(
    upload.operacoes ?? [],
  );
  // Importado, com linhas brutas no doc e nenhuma referência gravada: ou o
  // enriquecimento ainda roda, ou morreu no meio. O gatilho tem 540 s de
  // timeout; passado o prazo, é importação incompleta e excluir volta a valer
  // (havia 19 statements assim em produção, um deles com 96 linhas).
  const semOperacoesGravadas =
    (upload.operacoes?.length ?? 0) > 0 && !upload.operacoesImportadas;
  const prazo = upload.dataUpload
    ? upload.dataUpload.clone().add(PRAZO_PROCESSAMENTO_MIN, "minutes")
    : undefined;
  // Sem novos snapshots o componente não renderiza sozinho: um timer força a
  // reavaliação no instante em que o prazo vence, para liberar o excluir.
  const [, setVencido] = useState(false);
  useEffect(() => {
    if (!semOperacoesGravadas || !prazo) return;
    const restante = prazo.diff(moment());
    if (restante <= 0) return;
    const timer = setTimeout(() => setVencido(true), restante);
    return () => clearTimeout(timer);
  }, [semOperacoesGravadas, prazo?.valueOf()]);
  const processando =
    semOperacoesGravadas && !!prazo && moment().isBefore(prazo);
  const incompleta = semOperacoesGravadas && !processando;

  /*
   * Caso o statement já tenha sido importado, as operações não estão mais presentes no documento
   * do upload. Precisamos buscá-las na coleção /operacoesInternacionais filtrando pelo uploadId.
   */
  useEffect(() => {
    if (!!visualizado && operacoes.length === 0) {
      const db = new OperacaoInternacionalDb();
      const q = query(
        db.collection({ usuarioId: visualizado, operacaoInternacionalId: "" }),
        where("uploadId", "==", upload.id),
      );
      return onSnapshot(q, (snapshot) => {
        setOperacoes(
          snapshot.docs.map((doc) => doc.data() as OperacaoInternacional),
        );
      });
    }
  }, [visualizado, upload.id]);

  const exportarCsv = () => {
    if (!operacoes || operacoes.length === 0) return;

    // Cabeçalho do CSV
    const header = [
      "Data",
      "Ativo",
      "Descrição",
      "Tipo",
      "Quantidade",
      "Valor",
      "Valor (R$)",
      "Moeda",
      "Crédito/Débito",
    ];

    // Linhas com dados
    const linhas = operacoes.map((op) => [
      moment(op.dataPregao).format("DD/MM/YY"),
      op.ativo ?? "",
      op.descricao,
      (op.tipoOperacao ?? "") + (op.duplicataDeUploadId ? " (repetida)" : ""),
      op.quantidade !== undefined
        ? op.quantidade.toLocaleString("pt-BR", { maximumFractionDigits: 3 })
        : "",
      op.valorOperacao.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      op.valorOperacaoBRL !== undefined
        ? op.valorOperacaoBRL.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
      op.moeda,
      op.debitoCredito === DebitoCredito.Debito ? "Débito" : "Crédito",
    ]);

    // Monta string CSV
    const csvContent = [header, ...linhas]
      .map((linha) =>
        linha
          .map((campo) => {
            const texto = String(campo).replace(/"/g, '""');
            return `"${texto}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const nomeArquivo = `statement_${upload.id}.csv`;
    link.setAttribute("download", nomeArquivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <ListItem disableGutters sx={{ px: 2, py: 1 }}>
      <Box width="100%">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
          width="100%"
        >
          <ButtonBase
            disableRipple
            onClick={() => setExpandido((prev) => !prev)}
          >
            <Icon
              fontSize="inherit"
              sx={(theme) => ({
                // Accent como TINTA, nunca `primary.main` em texto/ícone.
                color: theme.palette.accentInk,
                transition: theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
                transform: expandido ? "rotate(90deg)" : "",
                width: 16,
              })}
            >
              <IconChevronRight size={16} strokeWidth={3} />
            </Icon>
          </ButtonBase>

          <Box display="flex" flexGrow={1} px={1}>
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              Statement importado:{" "}
              <Typography
                variant="dataMono"
                component="span"
                color="text.primary"
              >
                {upload.nomeArquivoOriginal}
              </Typography>
              {upload.periodo && (
                <>
                  {" - "}
                  <Typography
                    variant="dataMono"
                    component="span"
                    fontWeight={500}
                    sx={(theme) => ({ color: theme.palette.accentInk })}
                  >
                    {upload.periodo.de.format("DD/MM/YY")}
                  </Typography>
                  {" a "}
                  <Typography
                    variant="dataMono"
                    component="span"
                    fontWeight={500}
                    sx={(theme) => ({ color: theme.palette.accentInk })}
                  >
                    {upload.periodo.a.format("DD/MM/YY")}
                  </Typography>
                </>
              )}
            </Typography>
            {processando && (
              <Chip
                label="processando"
                size="small"
                sx={(theme) => ({
                  ml: 1,
                  backgroundColor: theme.palette.statusChip.accent.bg,
                  color: theme.palette.statusChip.accent.fg,
                })}
              />
            )}
            {incompleta && (
              <Tooltip title="A importação não terminou: nenhuma operação foi gravada. Exclua o statement e envie de novo.">
                <Chip
                  label="importação incompleta"
                  size="small"
                  sx={(theme) => ({
                    ml: 1,
                    backgroundColor: theme.palette.statusChip.warn.bg,
                    color: theme.palette.statusChip.warn.fg,
                  })}
                />
              </Tooltip>
            )}
            {!!upload.operacoesIgnoradas && (
              <Tooltip title="Linhas que já existiam em outro statement da mesma corretora (período sobreposto) e foram ignoradas.">
                <Chip
                  label={`${upload.operacoesIgnoradas} ${upload.operacoesIgnoradas === 1 ? "repetida ignorada" : "repetidas ignoradas"}`}
                  size="small"
                  sx={(theme) => ({
                    ml: 1,
                    backgroundColor: theme.palette.statusChip.warn.bg,
                    color: theme.palette.statusChip.warn.fg,
                  })}
                />
              </Tooltip>
            )}
          </Box>

          <ButtonGroup size="small" variant="outlined">
            <Button
              color="primary"
              size="small"
              component={Link}
              to={`/internacional/statement/${upload.id}`}
            >
              <IconFileSearch size={16} />
            </Button>
            <Button color="secondary" size="small" onClick={exportarCsv}>
              <IconFileTypeCsv size={16} />
            </Button>
            <Button
              color="info"
              size="small"
              onClick={async () =>
                await getDownloadURL(
                  ref(
                    getStorage(),
                    `/usuarios/${visualizado}/uploadsInternacionais/${upload.id}`,
                  ),
                ).then((url) => window.open(url))
              }
            >
              <IconCloudDownload size={16} />
            </Button>
            {/* Enquanto o enriquecimento roda (operacoes ainda no doc, sem
                operacoesImportadas), apagar deixaria as operações gravadas
                depois sem statement — órfãs contadas no cálculo. */}
            <Tooltip
              title={
                processando
                  ? "Processando as operações. Aguarde para excluir."
                  : "Excluir statement"
              }
            >
              <span>
                <Button
                  color="error"
                  size="small"
                  disabled={processando}
                  onClick={async () => {
                    if (!!visualizado)
                      await deleteDoc(
                        new UploadInternacionalDb().doc({
                          usuarioId: visualizado,
                          uploadId: upload.id,
                        }),
                      );
                  }}
                >
                  <IconTrashX size={16} />
                </Button>
              </span>
            </Tooltip>
          </ButtonGroup>
        </Box>
        <Collapse in={expandido}>
          <Grid container spacing={1} sx={{ py: 1, pl: 3 }}>
            <Grid item xs={12}>
              {operacoes && operacoes.length > 0 ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Ativo</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Qtd</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="right">Valor (R$)</TableCell>
                        <TableCell align="center">Moeda</TableCell>
                        <TableCell align="center">Crédito/Débito</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {map(operacoes, (operacao) => {
                        const debito =
                          operacao.debitoCredito === DebitoCredito.Debito;
                        return (
                          <TableRow key={operacao.id}>
                            <TableCell>
                              <Typography
                                variant="dataMono"
                                color="text.secondary"
                              >
                                {moment(operacao.dataPregao).format("DD/MM/YY")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="dataMono" fontWeight={500}>
                                {operacao.ativo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {operacao.descricao}
                              {operacao.duplicataDeUploadId && (
                                <Tooltip title="Linha igual já importada de outro statement desta corretora. Não entra no cálculo enquanto aquele statement existir.">
                                  <Chip
                                    label="repetida"
                                    size="small"
                                    sx={(theme) => ({
                                      ml: 1,
                                      backgroundColor:
                                        theme.palette.statusChip.warn.bg,
                                      color: theme.palette.statusChip.warn.fg,
                                    })}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>{operacao.tipoOperacao}</TableCell>
                            <TableCell align="right">
                              <Typography variant="dataMono">
                                {operacao.quantidade !== undefined
                                  ? operacao.quantidade.toLocaleString(
                                      "pt-BR",
                                      { maximumFractionDigits: 3 },
                                    )
                                  : ""}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {/* Sinal do valor vem da cor semântica, não de um
                                  fundo tingido na linha inteira. */}
                              <Typography
                                variant="dataMono"
                                color={debito ? "error.main" : "success.main"}
                              >
                                {operacao.valorOperacao.toLocaleString(
                                  "pt-BR",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="dataMono"
                                color="text.secondary"
                              >
                                {operacao.valorOperacaoBRL !== undefined
                                  ? operacao.valorOperacaoBRL.toLocaleString(
                                      "pt-BR",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      },
                                    )
                                  : ""}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="dataMono"
                                color="text.secondary"
                              >
                                {operacao.moeda}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={debito ? "Débito" : "Crédito"}
                                sx={(theme) => ({
                                  bgcolor: debito
                                    ? theme.palette.statusChip.destructive.bg
                                    : theme.palette.statusChip.positive.bg,
                                  color: debito
                                    ? theme.palette.statusChip.destructive.fg
                                    : theme.palette.statusChip.positive.fg,
                                })}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography variant="labelMono" color="text.secondary">
                  Nenhuma operação encontrada neste statement
                </Typography>
              )}
            </Grid>
          </Grid>
        </Collapse>
      </Box>
    </ListItem>
  );
}
